import React, { useContext, useState } from "react";
import "../../styles/learningPathCard.css";
import { WebsiteContext } from "../../context/AppContext";
import ContentComponent from "../common/ContentComponent";
import booksImage from "../../images/content_Image.png";
import { IoCheckmarkCircle } from "react-icons/io5";
import { LuRefreshCw } from "react-icons/lu";
import { RiSeedlingLine, RiFlashlightLine, RiRocketLine, RiVipCrownLine } from "react-icons/ri";
import { motion, AnimatePresence } from "framer-motion";

const levelsData = [
  {
    id: "beginner",
    title: "Beginner Level",
    cefr: "A1 - A2 Level",
    icon: <RiSeedlingLine size={16} />,
    desc: "Build strong conversational foundations. Master everyday essentials, basic greetings, common questions, and essential sentence patterns.",
    progressPercent: 25,
    modules: [
      { name: "Daily Greetings & Self-Introductions", lessons: "8 Interactive Lessons" },
      { name: "Essential Daily Vocabulary & Prepositions", lessons: "12 Practical Drills" },
      { name: "Simple Present & Past Tenses in Context", lessons: "10 Speaking Dialogues" },
    ],
  },
  {
    id: "intermediate",
    title: "Intermediate Level",
    cefr: "B1 - B2 Level",
    icon: <RiFlashlightLine size={16} />,
    desc: "Speak naturally in social and workplace settings. Express opinions, handle unexpected situations, and master common idioms.",
    progressPercent: 50,
    modules: [
      { name: "Opinion & Argument Structuring", lessons: "14 Real-world Scenarios" },
      { name: "Everyday Phrasal Verbs & Natural Idioms", lessons: "16 Context Quizzes" },
      { name: "Smooth Storytelling & Sentence Linking", lessons: "12 Speaking Checkpoints" },
    ],
  },
  {
    id: "advanced",
    title: "Advanced Level",
    cefr: "C1 Level",
    icon: <RiRocketLine size={16} />,
    desc: "Refine subtlety, nuance, and professional eloquence. Deliver presentations, navigate complex discussions, and eliminate hesitation.",
    progressPercent: 75,
    modules: [
      { name: "Executive Business Communication", lessons: "15 Presentation Drills" },
      { name: "Advanced Nuance, Subtlety & Humor", lessons: "10 Listening Audios" },
      { name: "Debate & Persuasive Speaking Techniques", lessons: "14 Interactive Challenges" },
    ],
  },
  {
    id: "pro",
    title: "Pro Level",
    cefr: "C2 Native Mastery",
    icon: <RiVipCrownLine size={16} />,
    desc: "Achieve native-tier spontaneity and commanding articulation. Master rhetorical devices, fast-paced dialogue, and cultural colloquialisms.",
    progressPercent: 100,
    modules: [
      { name: "Commanding Public Address & Rhetoric", lessons: "18 Masterclasses" },
      { name: "Rapid-fire Spontaneous Discussions", lessons: "20 AI Simulations" },
      { name: "Cultural Fluency & Colloquial Cadence", lessons: "15 Deep Immersion Units" },
    ],
  },
];

const LearningPathSection = () => {
  const { isMobileView, dark } = useContext(WebsiteContext);
  const [selectedLevelIndex, setSelectedLevelIndex] = useState(0);

  const currentLevel = levelsData[selectedLevelIndex];

  const handleNextLevel = () => {
    setSelectedLevelIndex((prev) => (prev + 1) % levelsData.length);
  };

  return (
    <div className={isMobileView ? "learning_path_section_mobile" : "learning_path_section"}>
      {/* 560px Desktop Card on the left for alternating rhythm */}
      <div className="learning_path_card_wrapper">
        <img src={booksImage} alt="3D Books Stack" className="learning_path_3d_badge" />

        <div className={`learning_path_card ${dark ? "learning_path_card_dark" : "learning_path_card_light"}`}>
          {/* 4 Level Switcher Tabs */}
          <div className="level_tabs_grid">
            {levelsData.map((lvl, index) => (
              <button
                key={lvl.id}
                className={`level_tab_btn ${selectedLevelIndex === index ? "level_tab_btn_active" : ""}`}
                onClick={() => setSelectedLevelIndex(index)}
              >
                {lvl.icon}
                <span>{lvl.title.replace(" Level", "")}</span>
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={currentLevel.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
            >
              {/* Level Details Header */}
              <div className="level_header_box">
                <div className="level_title_row">
                  <span className="level_title">
                    {currentLevel.icon} {currentLevel.title}
                  </span>
                  <span className="level_cefr_badge">{currentLevel.cefr}</span>
                </div>
                <p className="level_desc">{currentLevel.desc}</p>

                {/* Progress Bar */}
                <div className="level_progress_row">
                  <div className="progress_track">
                    <div
                      className="progress_fill"
                      style={{ width: `${currentLevel.progressPercent}%` }}
                    ></div>
                  </div>
                  <span className="progress_text">{currentLevel.progressPercent}% Path Milestone</span>
                </div>
              </div>

              {/* Modules Checklist */}
              <div className="modules_list">
                {currentLevel.modules.map((mod, idx) => (
                  <div key={idx} className="module_item">
                    <div className="module_name">
                      <IoCheckmarkCircle size={15} className="module_status_icon" />
                      <span>{mod.name}</span>
                    </div>
                    <span className="module_lessons_count">{mod.lessons}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Footer Bar */}
          <div className="learning_path_footer">
            <span className="learning_path_hint">Tap levels above to preview roadmap</span>
            <button className="learning_path_next_btn" onClick={handleNextLevel} title="Next Level">
              <LuRefreshCw size={12} /> Next Level
            </button>
          </div>
        </div>
      </div>

      {/* Description on the right */}
      <div className="learning_path_text_wrapper">
        <ContentComponent
          title={"AI Learning Path: 4 Structured Stages from Beginner to Pro"}
          content={
            "Chart your language journey with complete confidence. Fluent Fable divides English fluency into four guided stages—Beginner, Intermediate, Advanced, and Pro—with clear milestones, interactive checkpoints, and measurable progress at every step."
          }
        />
      </div>
    </div>
  );
};

export default LearningPathSection;
